import { useMemo } from 'react';
import { moduleRegistry, type RouteDefinition } from '@em-cms/core';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, FileText, BookOpen, Briefcase, GalleryHorizontal,
  UsersRound, HelpCircle, Package, ClipboardList, MessageSquare,
  Image, Users, Settings, Palette, Navigation, HardDrive, BarChart3, Globe,
  Server, Database, ArrowLeftRight, Bell, Zap, FolderOpen, Plus, Download,
} from 'lucide-react';

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard, FileText, BookOpen, Briefcase, GalleryHorizontal,
  UsersRound, HelpCircle, Package, ClipboardList, MessageSquare,
  Image, Users, Settings, Palette, Navigation, HardDrive, BarChart3, Globe,
  Server, Database, ArrowLeftRight, Bell, Zap, FolderOpen, Plus, Download,
};

// Default icons for modules without icon
const defaultModuleIcons: Record<string, LucideIcon> = {
  blog: BookOpen,
  forms: ClipboardList,
  products: Package,
  projects: Briefcase,
  content: FileText,
  storage: HardDrive,
  analytics: BarChart3,
  seo: Globe,
};

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon | null;
  permission?: string;
  moduleId: string;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

export function useModuleNavigation(enabledModules: string[], isSuperAdmin: boolean) {
  return useMemo(() => {
    const allModules = moduleRegistry.getAll();
    const activeModules = isSuperAdmin
      ? allModules
      : allModules.filter((m: { id: string }) => enabledModules.includes(m.id));

    // Create a group for each module (not category)
    const groups: NavGroup[] = activeModules.map((module: { 
      id: string; 
      name: string; 
      icon?: string;
      adminRoutes?: RouteDefinition[] 
    }) => {
      // Get module icon or default
      const ModuleIcon = module.icon 
        ? (iconMap[module.icon] || defaultModuleIcons[module.id] || FileText)
        : (defaultModuleIcons[module.id] || FileText);

      // Get all routes for this module (including sub-routes)
      const items = (module.adminRoutes || [])
        .filter((route: RouteDefinition) => {
          // Filter out parameter routes like /edit/:id
          return !route.path.includes('/:');
        })
        .map((route: RouteDefinition) => {
          const IconComponent = route.icon ? iconMap[route.icon] : null;
          return {
            to: route.path,
            label: route.label,
            icon: IconComponent,
            permission: route.permission,
            moduleId: module.id,
          };
        });

      return {
        id: module.id,
        label: module.name,
        icon: ModuleIcon,
        items,
      };
    });

    // Return only groups with items
    return groups.filter(g => g.items.length > 0);
  }, [enabledModules, isSuperAdmin]);
}

export function useAllModuleRoutes() {
  return useMemo(() => {
    return moduleRegistry.getAll().flatMap((m: { id: string; adminRoutes?: RouteDefinition[] }) =>
      (m.adminRoutes || []).map((r: RouteDefinition) => ({
        ...r,
        moduleId: m.id,
      }))
    );
  }, []);
}
